import { useQuery } from '@tanstack/react-query';
import { Lock, Printer } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { useSetCrumbs } from '../../lib/crumbs';
import { reportClientError } from '../../lib/errors';
import { NAV_PATH } from '../../lib/nav';
import { useRole } from '../../lib/role';
import type { AuthedPageProps } from '../../page-route';
import { AnswerDetails, AnswerText } from '../Rules/AnswerText';
import { LoadError, shouldReport } from '../Rules/LoadError';
import { fieldIndex } from '../Rules/answer';
import { LABEL, T, say } from '../Rules/texts';

/*
 * «Скачать правила на дату» — документ для печати `/rules/print?date=` (004, FR-013, T048; research R19;
 * contracts/ui.md, «Документ для печати»): правила на дату — номер версии, дата начала, основание, разделы, вопросы,
 * ответы, закон со статьёй. Без примеров дней, ФИО, черновика и кнопок. `@media print`: A4, поля 15 мм, без меню и
 * шапки сервиса. Страница сразу открывает диалог печати; человек печатает или сохраняет файл средствами браузера.
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/u;

/** Печать: только документ, лист A4 с полями 15 мм; каркас сервиса (меню, шапка, нижняя навигация) не печатается. */
const PRINT_CSS = `
@page { size: A4; margin: 15mm; }
@media print {
  html, body, #root { height: auto !important; overflow: visible !important; background: var(--color-surface) !important; }
  body * { visibility: hidden; }
  [data-print-root], [data-print-root] * { visibility: visible; }
  [data-print-root] { position: absolute; top: 0; left: 0; width: 100%; max-width: none; margin: 0; padding: 0; }
  [data-print-hide] { display: none !important; }
  [data-print-root] h2, [data-print-root] h3 { break-after: avoid; }
  [data-print-root] article { break-inside: avoid; }
}
`;

export function RulesPrintPage({ me }: AuthedPageProps) {
  useSetCrumbs([{ label: LABEL.title, to: NAV_PATH.rules }, { label: LABEL.printTitle }]);
  const { role } = useRole();
  const [params] = useSearchParams();
  const raw = params.get('date');
  const date = raw && ISO.test(raw) ? raw : undefined;
  const q = useQuery({
    queryKey: rulesKeys.view(role, { date }),
    queryFn: async () => {
      try {
        return await api.rules({ date });
      } catch (e) {
        if (shouldReport(e)) reportClientError('RulesPrint', 'load rules', e);
        throw e;
      }
    },
  });
  const view = q.data;
  const fields = useMemo(() => fieldIndex(view?.groups ?? []), [view]);

  // Диалог печати — один раз, когда документ отрисован.
  const printed = useRef(false);
  useEffect(() => {
    if (!view || printed.current) return;
    const t = setTimeout(() => {
      printed.current = true;
      window.print();
    }, 150);
    return () => clearTimeout(t);
  }, [view]);

  if (q.isLoading) return <p className="text-[13px] text-ink-3">{LABEL.loading}</p>;
  if (q.error || !view)
    return <LoadError error={q.error} title={LABEL.loadError} onRetry={() => void q.refetch()} />;

  const groups = view.groups.filter((g) => !g.inCatalog || g.law).sort((a, b) => a.order - b.order);
  const sections = [...view.sections].sort((a, b) => a.n - b.n);
  return (
    <div data-print-root className="mx-auto w-full max-w-[800px] space-y-6 text-ink">
      <style>{PRINT_CSS}</style>
      <div data-print-hide className="flex justify-end">
        <Button icon={Printer} onClick={() => window.print()}>
          {LABEL.print}
        </Button>
      </div>
      <header className="space-y-1">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em] break-words">{`${LABEL.title} · ${me.object.name}`}</h1>
        <p className="text-[13.5px]">
          {say.printVersion(view.date, view.version.version, view.version.validFrom)}
        </p>
        <p className="text-[13.5px] break-words text-ink-2">{say.printBasis(view.version.basis)}</p>
      </header>
      {sections.map((s) => {
        const cards = groups.filter((g) => g.section === s.id);
        if (cards.length === 0) return null;
        return (
          <section key={s.id} className="space-y-3">
            <h2 className="border-b border-line pb-1 text-[15px] font-semibold">{`${s.n}. ${s.title}`}</h2>
            {cards.map((g) => (
              <article key={g.id} className="space-y-1">
                <h3 className="text-[14px] font-semibold break-words">{g.question}</h3>
                <AnswerText group={g} fields={fields} />
                <AnswerDetails group={g} fields={fields} />
                {g.law && (
                  <p className="flex items-start gap-1.5 text-[13px] text-ink-2">
                    <Lock size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" aria-hidden />
                    {`${g.law.article}. ${T.lawText}`}
                  </p>
                )}
                {g.fixed === 'system' && <p className="text-[13px] text-ink-2">{T.fixedText}</p>}
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}
